import React, { useState, useEffect, useMemo } from 'react';
import api from '../../config/api';
import { parseYMD, MONTH_NAMES, isTodayMonthDay, yearsInCurrentCalendarYear } from '../../utils/dateUtils';
import styles from './LsaPage.module.css';

const LsaPage = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthIndex, setMonthIndex] = useState(new Date().getMonth());

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.getAllEmployees();
        if (res.success) setEmployees(res.data);
      } catch { /* ignore — empty list falls back to "no one this month" state */ }
      finally { setLoading(false); }
    })();
  }, []);

  const monthHonorees = useMemo(() => {
    return employees
      .filter((e) => e.isActive && e.dateOfJoining)
      .map((e) => ({ ...e, parsed: parseYMD(e.dateOfJoining), years: yearsInCurrentCalendarYear(e.dateOfJoining) }))
      .filter((e) => e.parsed.month === monthIndex && e.years >= 1)
      .sort((a, b) => a.parsed.day - b.parsed.day);
  }, [employees, monthIndex]);

  const goPrevMonth = () => setMonthIndex((m) => (m + 11) % 12);
  const goNextMonth = () => setMonthIndex((m) => (m + 1) % 12);
  const goThisMonth = () => setMonthIndex(new Date().getMonth());

  return (
    <div className={styles.container}>
      <div className={styles.pageHeader}>
        <div className={styles.pageTitle}>🎖️ Long Service Awards</div>
        <div className={styles.pageSubtitle}>Recognizing loyalty and years of service completed.</div>
      </div>

      <div className={styles.monthNav}>
        <button className={styles.navBtn} onClick={goPrevMonth} aria-label="Previous month">←</button>
        <div className={styles.monthLabel}>
          {MONTH_NAMES[monthIndex]}
          {monthIndex !== new Date().getMonth() && (
            <button className={styles.todayLink} onClick={goThisMonth}>This month</button>
          )}
        </div>
        <button className={styles.navBtn} onClick={goNextMonth} aria-label="Next month">→</button>
      </div>

      {loading ? (
        <div className={styles.loading}>Loading...</div>
      ) : monthHonorees.length === 0 ? (
        <div className={styles.emptyState}>No work anniversaries in {MONTH_NAMES[monthIndex]}.</div>
      ) : (
        <div className={styles.grid}>
          {monthHonorees.map((e) => {
            const today = isTodayMonthDay(e.dateOfJoining);
            return (
              <div key={e.id} className={`${styles.card} ${today ? styles.today : ''}`}>
                {today && <div className={styles.todayBadge}>🎉 Today</div>}
                <div className={styles.avatar}>🏅</div>
                <div className={styles.name}>{e.name}</div>
                <div className={styles.years}>{e.years} {e.years === 1 ? 'Year' : 'Years'}</div>
                <div className={styles.date}>{e.parsed.day} {MONTH_NAMES[monthIndex].slice(0, 3)}</div>
                <div className={styles.dept}>{e.department}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LsaPage;
